import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { PersonStanding } from "lucide-react";
import logo from "./logo.png";
import AccessibilityPanel from "./AccessibilityPanel";

const HeaderA = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const firstMenuLinkRef = useRef(null);
  const hamburgerRef = useRef(null);
  const prevMenuOpenRef = useRef(false);

  const getStoredLanguage = () => {
    const fromAppLang = localStorage.getItem("app_lang");
    if (fromAppLang === "el" || fromAppLang === "en") return fromAppLang;

    const fromAuthLang = localStorage.getItem("language");
    if (fromAuthLang === "el" || fromAuthLang === "en") return fromAuthLang;

    return "el";
  };

  //Initialize language preference from localStorage, defaults to 'el' (Greek) if not previously set
  const [language, setLanguage] = useState(getStoredLanguage());

  // Accessibility panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const a11yWrapperRef = useRef(null);

  // Close panel on outside click
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (a11yWrapperRef.current && !a11yWrapperRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // Close panel on Escape
  useEffect(() => {
    if (!panelOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setPanelOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [panelOpen]);

  const baseClass = "font-bold text-sm md:text-xl px-3 py-1 rounded-md transition whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-custom-letters";

  //Header translations: Menu labels in Greek and English
  const translations = {
    el: { about: "Σχετικά με εμάς", contact: "Επικοινωνία" },
    en: { about: "About us", contact: "Contact us" }
  };

  const t = translations[language];

  //Toggle language function: Updates both local state and localStorage for persistence
  const toggleLanguage = () => {
    const newLang = language === "el" ? "en" : "el";
    setLanguage(newLang);
    localStorage.setItem("app_lang", newLang);
    localStorage.setItem("language", newLang);

    window.dispatchEvent(new Event("storage")); 
  };

  // Move focus into first link on open; return focus to hamburger on close
  useEffect(() => {
    if (menuOpen && firstMenuLinkRef.current) {
      firstMenuLinkRef.current.focus();
    } else if (!menuOpen && prevMenuOpenRef.current && hamburgerRef.current) {
      hamburgerRef.current.focus();
    }
    prevMenuOpenRef.current = menuOpen;
  }, [menuOpen]);

  // Close menu with Escape key and return focus to hamburger
  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  const renderMenuLinks = (isMobile = false) => (
    <>
      <NavLink
        ref={isMobile ? firstMenuLinkRef : null}
        to="/about"
        role={isMobile ? "menuitem" : undefined}
        className={({ isActive }) =>
          `${baseClass} ${isActive ? "bg-custom-background text-custom-grad2" : "text-custom-grad2 hover:underline"}`
        }
      >
        {t.about}
      </NavLink>
      <NavLink
        to="/contact"
        role={isMobile ? "menuitem" : undefined}
        className={({ isActive }) =>
          `${baseClass} ${isActive ? "bg-custom-background text-custom-grad2" : "text-custom-grad2 hover:underline"}`
        }
      >
        {t.contact}
      </NavLink>
    </>
  );

  return (
    <nav aria-label={language === "el" ? "Κύρια πλοήγηση" : "Main navigation"} className="w-full bg-custom-header px-4 md:px-8 py-4 shadow-md sticky top-0 z-50 flex items-center justify-between">
      {/* Skip to main content — visible only on keyboard focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-custom-background focus:text-custom-letters focus:px-4 focus:py-2 focus:rounded focus:font-bold"
      >
        {language === "el" ? "Μετάβαση στο κύριο περιεχόμενο" : "Skip to main content"}
      </a>

      <div className="flex items-center space-x-6">
        {/* Logo */}
        <NavLink to="/" aria-label={language === "el" ? "Αρχική σελίδα" : "Go to homepage"} className="flex items-center space-x-2 rounded focus:outline-none focus:ring-2 focus:ring-custom-letters">
          <img src={logo} alt="" className="h-14 w-auto md:h-16" />
          <span className="hidden sm:inline font-bold text-custom-letters text-2xl">
            ShareTheDeal
          </span>
        </NavLink>

        {/* Mobile Toggle */}
        <button
          ref={hamburgerRef}
          className="md:hidden text-custom-letters text-3xl font-bold focus:outline-none focus:ring-2 focus:ring-custom-letters rounded"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen
            ? (language === "el" ? "Κλείσιμο μενού πλοήγησης" : "Close navigation menu")
            : (language === "el" ? "Άνοιγμα μενού πλοήγησης" : "Open navigation menu")}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          ☰
        </button>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-6">
          {renderMenuLinks(false)}
        </div>

        {/* Mobile Dropdown */}
        {menuOpen && (
          <div
            id="mobile-menu"
            role="menu"
            aria-label={language === "el" ? "Μενού πλοήγησης" : "Navigation menu"}
            className="absolute top-full left-0 mt-2 min-w-fit bg-custom-header rounded-lg shadow-lg md:hidden z-50 border border-custom-grad1/20"
          >
            <div className="flex flex-col p-3 space-y-3">
              {renderMenuLinks(true)}
            </div>
          </div>
        )}
      </div>

      {/* Accessibility + Language Toggle */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Accessibility Panel Toggle */}
        <div ref={a11yWrapperRef} className="relative">
          <button
            onClick={() => setPanelOpen((v) => !v)}
            aria-pressed={panelOpen}
            aria-expanded={panelOpen}
            aria-haspopup="dialog"
            aria-label={language === "el"
              ? (panelOpen ? "Κλείσιμο πίνακα προσβασιμότητας" : "Άνοιγμα πίνακα προσβασιμότητας")
              : (panelOpen ? "Close accessibility panel" : "Open accessibility panel")}
            className="a11y-open-btn hover:scale-110 transition flex flex-col items-center group focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full"
          >
            <div className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full border-2 font-bold text-xs transition-colors ${
              panelOpen
                ? "bg-custom-grad1 border-custom-grad1 text-white"
                : "bg-custom-background border-custom-letters text-custom-letters group-hover:border-custom-grad2"
            }`}>
              <PersonStanding size={20} />
            </div>
          </button>
          {panelOpen && (
            <AccessibilityPanel language={language} className="top-full right-0 mt-2" />
          )}
        </div>

        {/* Language Toggle */}
        <button
          onClick={toggleLanguage}
          className="hover:scale-110 transition flex flex-col items-center group focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full"
          aria-label={language === "el" ? "Αλλαγή γλώσσας, τρέχουσα γλώσσα: Ελληνικά" : "Change language, current language: English"}
        >
          <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-custom-background rounded-full border-2 border-custom-letters font-bold text-xs text-custom-letters group-hover:border-custom-grad2 transition-colors">
            {language.toUpperCase()}
          </div>
        </button>
      </div>
    </nav>
  );
};

export default HeaderA;